import { useForm, usePage } from "@inertiajs/react";
import { getLocalTimeZone, parseDate, today } from "@internationalized/date";
import { accountPeoplePath } from "@travily/routes";
import { type AddPersonAudience, AddPersonSheet as AddPersonSheetView } from "@ui/components/AddPersonSheet";
import type { DateValue } from "@ui/primitives/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { useImageUploadField } from "@/hooks/use-image-upload-field";
import type { SharedProps } from "@/types";

/** Most people added to a household are children, so seed the year spinner there. */
const DEFAULT_SEED_AGE_YEARS = 8;

export interface AddPersonSheetProps {
  presented: boolean;
  onPresentedChange: (presented: boolean) => void;
  /** Fires once the refreshed roster contains the new person — select them. */
  onAdded?: (personId: number) => void;
}

/**
 * Adds a person to the current household from the SearchBar "Who" picker or the
 * trip "who visited" strip. Owns the form, the direct upload, and resolving the
 * created person out of the refreshed `family_members` shared prop.
 */
export function AddPersonSheet({ presented, onPresentedChange, onAdded }: AddPersonSheetProps) {
  const { family_members } = usePage<SharedProps>().props;
  const now = today(getLocalTimeZone());

  const [audience, setAudience] = useState<AddPersonAudience>("family");
  const [errorFocusToken, setErrorFocusToken] = useState(0);
  // Ids present when the request went out; the one that appears afterwards is ours.
  const rosterBeforeSubmit = useRef<Set<number> | null>(null);

  const form = useForm({
    name: "",
    date_of_birth: "",
    avatar_signed_id: null as string | null,
    title: "",
    guest: false,
  })
    .withPrecognition("post", accountPeoplePath())
    .setValidationTimeout(400);

  const formRef = useRef(form);
  formRef.current = form;

  const avatarField = useImageUploadField({
    onUploaded: (signedId) => formRef.current.setData("avatar_signed_id", signedId),
    onRemoved: () => formRef.current.setData("avatar_signed_id", null),
  });

  const avatarFieldRef = useRef(avatarField);
  avatarFieldRef.current = avatarField;

  const resetSheet = useCallback(() => {
    formRef.current.reset();
    setAudience("family");
    // form.reset() only knows about form data — the preview lives in the upload
    // hook, and left alone it greets the next person with the last one's photo.
    avatarFieldRef.current.setPreview(null);
  }, []);

  // The roster refresh arrives with the redirect's props, after onSuccess — so
  // watch the prop rather than reading it inside the submit callback.
  useEffect(() => {
    const before = rosterBeforeSubmit.current;
    if (!before) return;

    const added = family_members.find((member) => !before.has(member.id));
    if (!added) return;

    rosterBeforeSubmit.current = null;
    onAdded?.(added.id);
    resetSheet();
    onPresentedChange(false);
  }, [family_members, onAdded, onPresentedChange, resetSheet]);

  const handleAudienceChange = useCallback((next: AddPersonAudience) => {
    setAudience(next);
    formRef.current.setData("guest", next === "guest");
    if (next === "family") formRef.current.setData("title", "");
  }, []);

  const handleNameChange = useCallback((value: string) => {
    formRef.current.setData("name", value);
  }, []);

  const handleDateOfBirthChange = useCallback((value: DateValue | null) => {
    formRef.current.setData("date_of_birth", value ? value.toString() : "");
    // Drop a stale error the moment the date reads as complete. Validation still
    // waits for blur (below), but leaving the error up over a filled-in field
    // tells the person to fix something that is already fixed.
    if (value) formRef.current.forgetError("date_of_birth");
  }, []);

  // Blur, never change: a date field emits every intermediate complete date while
  // the year is typed (0001, 0019, 0199, 1990…), which floods and races the server.
  const handleDateOfBirthBlur = useCallback(() => {
    formRef.current.validate("date_of_birth");
  }, []);

  const handleSubmit = useCallback(() => {
    rosterBeforeSubmit.current = new Set(family_members.map((member) => member.id));
    formRef.current.post(accountPeoplePath(), {
      preserveScroll: true,
      preserveState: true,
      onError: () => setErrorFocusToken((token) => token + 1),
    });
  }, [family_members]);

  const dateOfBirth = form.data.date_of_birth ? parseDate(form.data.date_of_birth) : null;

  return (
    <AddPersonSheetView
      audience={audience}
      avatarPreview={avatarField.previewUrl}
      dateOfBirth={dateOfBirth}
      dateOfBirthError={form.invalid("date_of_birth") ? form.errors.date_of_birth : undefined}
      errorFocusToken={errorFocusToken}
      isPending={form.processing}
      maxDateOfBirth={now}
      name={form.data.name}
      nameError={form.invalid("name") ? form.errors.name : undefined}
      onAudienceChange={handleAudienceChange}
      onAvatarFileSelect={avatarField.onFileSelect}
      onAvatarRemove={avatarField.onRemove}
      onDateOfBirthBlur={handleDateOfBirthBlur}
      onDateOfBirthChange={handleDateOfBirthChange}
      onNameChange={handleNameChange}
      onPresentedChange={onPresentedChange}
      onRelationshipChange={(value) => formRef.current.setData("title", value)}
      onSubmit={handleSubmit}
      placeholderDateOfBirth={now.subtract({ years: DEFAULT_SEED_AGE_YEARS })}
      presented={presented}
      relationship={form.data.title}
    />
  );
}
